// react
import * as React from "react";
import { connect } from "react-redux";
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from "@fluentui/react-components";
import { Delete16Regular, DocumentBulletListRegular, CheckmarkCircle16Filled, DismissCircle16Filled, ChevronDown16Regular, ChevronRight16Regular } from "@fluentui/react-icons";

//styles
import "./VisitFormConceptPayable.Styles.css";

// component functions
import * as ComponentFunctions from "./VisitFormConceptPayable.Functions";

// models
import { ContractConceptRelationship } from "../../../models/Entities/Contracts/ContractConceptRelationship";
import ConfirmDialog from "../../../commons/component/ConfirmDialog/ConfirmDialog";


const VisitFormConceptPayableListComponent = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();

    React.useEffect(() => {
        if (!props.LoadingSuccess) {
            props.getAllContractConceptRelationships(props.ContractId);
        }
        props.GetAllContractsConcept(props.ContractId, undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ContractId]);

    React.useEffect(() => {
        if (props.contractConcepts.length === 0 && props.ContractId) {
            props.GetAllContractsConcept(props.ContractId, undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.contractConcepts.length]);

    const [deletingId, setDeletingId] = React.useState<number | undefined>(undefined);
    const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
    const [pendingDeleteId, setPendingDeleteId] = React.useState<number | undefined>(undefined);
    const [pendingDeleteItem, setPendingDeleteItem] = React.useState<ContractConceptRelationship | undefined>(undefined);
    const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

    React.useEffect(() => {
        if (props.successDeleting) {
            props.getAllContractConceptRelationships(props.ContractId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.successDeleting, props.ContractId]);

    React.useEffect(() => {
        if (!props.isDeleting) {
            setDeletingId(undefined);
        }
    }, [props.isDeleting]);

    const concepts = Array.isArray(props.concepts) ? props.concepts : [];

    const uniqueConcepts = React.useMemo(() => {
        const map = new Map<string, ContractConceptRelationship>();
        for (const c of concepts) {
            const key = `${(c.formConcept?.toLowerCase() ?? '').trim()}|${(c.formName?.toLowerCase() ?? '').trim()}|${c.itemNumber ?? ''}`;
            if (!map.has(key)) {
                map.set(key, c);
            }
        }
        return Array.from(map.values());
    }, [concepts]);

    const payableCount = uniqueConcepts.filter(c => c.isPayable === true).length;
    const notPayableCount = uniqueConcepts.length - payableCount;

    const toggleExpand = (key: string) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <div className="vfcp-container">
            <div className="vfcp-header">
                <div className="vfcp-header-titleBlock">
                    <span className="vfcp-header-eyebrow">
                        {t('contractAssociation.visitFormConceptPayableList.eyebrow', 'Conceptos del formulario')}
                    </span>
                    <span className="vfcp-header-title">
                        {uniqueConcepts.length}{' '}
                        <span className="vfcp-header-titleSuffix">
                            {uniqueConcepts.length === 1
                                ? t('contractAssociation.visitFormConceptPayableList.itemSingular', 'concepto')
                                : t('contractAssociation.visitFormConceptPayableList.itemPlural', 'conceptos')}
                        </span>
                    </span>
                </div>
                {uniqueConcepts.length > 0 && (
                    <div className="vfcp-header-stats">
                        <span className="vfcp-stat vfcp-stat--payable">
                            <CheckmarkCircle16Filled />
                            <span className="vfcp-stat-value">{payableCount}</span>
                            <span className="vfcp-stat-label">{t('contractAssociation.visitFormConceptPayableList.payable', 'Liquidables')}</span>
                        </span>
                        <span className="vfcp-stat vfcp-stat--notPayable">
                            <DismissCircle16Filled />
                            <span className="vfcp-stat-value">{notPayableCount}</span>
                            <span className="vfcp-stat-label">{t('contractAssociation.visitFormConceptPayableList.notPayable', 'No liquidables')}</span>
                        </span>
                    </div>
                )}
            </div>

            {props.isLoading && !props.LoadingSuccess ? (
                <div className="vfcp-state vfcp-state--loading">
                    <Spinner size="tiny" />
                    <span>{t('common.loading', 'Cargando...')}</span>
                </div>
            ) : uniqueConcepts.length === 0 ? (
                <div className="vfcp-state vfcp-state--empty">
                    <DocumentBulletListRegular />
                    <span>{t('contractAssociation.visitFormConceptPayableList.empty', 'Sin conceptos asociados.')}</span>
                </div>
            ) : (
                <ul className="vfcp-list">
                    {uniqueConcepts.map((concept: ContractConceptRelationship) => {
                        const rowKey = `${concept.formConcept}|${concept.formName ?? ''}|${concept.itemNumber ?? ''}`;
                        const isExpanded = expandedKeys.has(rowKey);
                        const isRowDeleting = props.isDeleting && deletingId === concept.id;
                        const isRealized = concept.formConcept?.trim().toLowerCase() === 'realized';

                        const allRelatedRelationships = concepts.filter(c =>
                            c.formConcept?.toLowerCase() === concept.formConcept?.toLowerCase() &&
                            (c.formName?.toLowerCase() ?? '') === (concept.formName?.toLowerCase() ?? '') &&
                            c.itemNumber === concept.itemNumber
                        );

                        const allConceptNames = allRelatedRelationships
                            .filter(r => r.contractConceptId !== undefined && r.contractConceptId !== null)
                            .map(r => {
                                const cc = props.contractConcepts.find(c => c.id === r.contractConceptId);
                                return { id: r.contractConceptId!, name: cc?.concept?.conceptName };
                            })
                            .filter(({ name }) => !!name);

                        const nameToShow = concept.formConceptNameToShow?.trim();

                        return (
                            <li
                                key={rowKey}
                                className={`vfcp-item ${concept.isPayable ? 'is-payable' : 'is-notPayable'} ${isRowDeleting ? 'is-deleting' : ''}`}
                            >
                                {/* Collapsed header — always visible */}
                                <button
                                    type="button"
                                    className="vfcp-item__trigger"
                                    onClick={() => toggleExpand(rowKey)}
                                    aria-expanded={isExpanded}
                                >
                                    <span className="vfcp-item__chevron">
                                        {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                                    </span>
                                    <span className={`vfcp-item__statusDot ${concept.isPayable ? 'is-payable' : 'is-notPayable'}`} aria-hidden="true" />
                                    <span className="vfcp-item__label" title={concept.formConcept}>
                                        {concept.formConcept}
                                    </span>
                                    {isRealized && concept.formName && (
                                        <span className="vfcp-item__badge vfcp-item__badge--form" title={concept.formName}>
                                            {concept.formName} #{(concept.itemNumber ?? 0) + 1}
                                        </span>
                                    )}
                                    <span className={`vfcp-item__badge ${concept.isPayable ? 'vfcp-item__badge--payable' : 'vfcp-item__badge--notPayable'}`}>
                                        {concept.isPayable
                                            ? <><CheckmarkCircle16Filled /> {t('contractAssociation.visitFormConceptPayableList.payable', 'Liquidable')}</>
                                            : <><DismissCircle16Filled /> {t('contractAssociation.visitFormConceptPayableList.notPayable', 'No liquidable')}</>}
                                    </span>
                                </button>

                                {/* Expanded detail */}
                                {isExpanded && (
                                    <div className="vfcp-item__detail">
                                        <dl className="vfcp-detail-grid">
                                            <dt>{t('contractAssociation.visitFormConceptPayableList.fieldFormConcept', 'Concepto de formulario')}</dt>
                                            <dd>{concept.formConcept}</dd>

                                            {concept.formName && (
                                                <>
                                                    <dt>{t('contractAssociation.visitFormConceptPayableList.fieldFormName', 'Formulario')}</dt>
                                                    <dd>{concept.formName}</dd>
                                                </>
                                            )}

                                            {concept.itemNumber !== undefined && concept.itemNumber !== null && (
                                                <>
                                                    <dt>{t('contractAssociation.visitFormConceptPayableList.fieldItemNumber', 'Ítem')}</dt>
                                                    <dd>#{concept.itemNumber + 1}</dd>
                                                </>
                                            )}

                                            {nameToShow && (
                                                <>
                                                    <dt>{t('contractAssociation.visitFormConceptPayableList.nameToShow', 'Nombre a mostrar')}</dt>
                                                    <dd>{nameToShow}</dd>
                                                </>
                                            )}

                                            {allConceptNames.length > 0 && (
                                                <>
                                                    <dt>{t('contractAssociation.visitFormConceptPayableList.conceptToSettle', 'Conceptos a liquidar')}</dt>
                                                    <dd>
                                                        <span className="vfcp-detail-conceptList">
                                                            {allConceptNames.map(({ id, name }) => (
                                                                <span key={id} className="vfcp-detail-conceptChip">{name}</span>
                                                            ))}
                                                        </span>
                                                    </dd>
                                                </>
                                            )}
                                        </dl>

                                        <div className="vfcp-item__actions">
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                title={t('common.delete', 'Eliminar')}
                                                icon={isRowDeleting ? <Spinner size="extra-tiny" /> : <Delete16Regular />}
                                                disabled={isRowDeleting}
                                                onClick={() => {
                                                    setPendingDeleteId(concept.id);
                                                    setPendingDeleteItem(concept);
                                                    setConfirmOpen(true);
                                                }}
                                            >
                                                {t('common.delete', 'Eliminar')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            <ConfirmDialog
                open={confirmOpen}
                title={t('contractAssociation.visitFormConceptPayableList.confirmDeleteTitle', 'Confirm deletion')}
                message={t('contractAssociation.visitFormConceptPayableList.confirmDeleteItemLabel', 'You are about to delete the item')}
                highlight={pendingDeleteItem ? pendingDeleteItem.formConcept : undefined}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                onCancel={() => { setConfirmOpen(false); setPendingDeleteId(undefined); setPendingDeleteItem(undefined); }}
                onConfirm={() => {
                    if (pendingDeleteId !== undefined) {
                        setConfirmOpen(false);
                        setDeletingId(pendingDeleteId);
                        props.deleteContractConceptRelationship?.(pendingDeleteId);
                    }
                }}
            />
        </div>
    );

}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(VisitFormConceptPayableListComponent as any);